/**
 * Session Cache Invalidation System
 * Handles intelligent cache invalidation strategies for auth state changes
 */

import { sessionEventSystem, SessionEventType } from "./session-event-system";
import { sessionCacheManager } from "./session-cache-manager";
import logger from "logger";

export type InvalidationStrategy =
  | "immediate"
  | "delayed"
  | "on-next-access"
  | "conditional";

export interface InvalidationRule {
  eventType: SessionEventType;
  strategy: InvalidationStrategy;
  delay?: number; // for delayed strategy
  condition?: (eventData: any, currentSession: any) => boolean; // for conditional strategy
}

export interface CacheInvalidationConfig {
  rules: InvalidationRule[];
  autoRefreshOnInvalidation: boolean;
  crossTabSynchronization: boolean;
  stalenessThreshold: number; // milliseconds
}

export interface SessionCacheInvalidation {
  configure(config: Partial<CacheInvalidationConfig>): void;
  addRule(rule: InvalidationRule): () => void;
  invalidateCache(reason: string, immediate?: boolean): void;
  checkAndRefreshStale(): Promise<boolean>;
  destroy(): void;
}

class SessionCacheInvalidationImpl implements SessionCacheInvalidation {
  private config: CacheInvalidationConfig = {
    rules: [
      // Default invalidation rules
      {
        eventType: "session-invalidated",
        strategy: "immediate",
      },
      {
        eventType: "auth-state-changed",
        strategy: "immediate",
      },
      {
        eventType: "session-changed",
        strategy: "conditional",
        condition: (eventData, currentSession) => {
          // Only invalidate if the session actually changed
          return JSON.stringify(eventData) !== JSON.stringify(currentSession);
        },
      },
    ],
    autoRefreshOnInvalidation: true,
    crossTabSynchronization: true,
    stalenessThreshold: 5 * 60 * 1000, // 5 minutes
  };

  private eventUnsubscribers: (() => void)[] = [];
  private refreshCallback: (() => Promise<void>) | null = null;
  private stalenessCheckInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.setupEventListeners();
    this.startStalenessCheck();
  }

  configure(config: Partial<CacheInvalidationConfig>): void {
    this.config = { ...this.config, ...config };

    // Restart event listeners with new config
    this.destroyEventListeners();
    this.setupEventListeners();

    // Restart staleness check with new threshold
    this.stopStalenessCheck();
    this.startStalenessCheck();
  }

  addRule(rule: InvalidationRule): () => void {
    this.config.rules.push(rule);

    // Set up listener for this specific rule
    const unsubscribe = sessionEventSystem.on(rule.eventType, (event) => {
      this.handleInvalidationEvent(rule, event);
    });

    this.eventUnsubscribers.push(unsubscribe);

    return () => {
      const index = this.config.rules.indexOf(rule);
      if (index > -1) {
        this.config.rules.splice(index, 1);
      }
      unsubscribe();
    };
  }

  invalidateCache(reason: string, immediate = true): void {
    logger.debug(`Invalidating session cache: ${reason}`);

    if (immediate) {
      sessionCacheManager.invalidate();

      // Emit invalidation event for cross-tab sync
      if (this.config.crossTabSynchronization) {
        sessionEventSystem.emit("cache-invalidated", { reason });
      }

      // Auto-refresh if configured
      if (this.config.autoRefreshOnInvalidation && this.refreshCallback) {
        this.refreshCallback().catch((error) => {
          logger.error(
            "Failed to auto-refresh session after invalidation:",
            error,
          );
        });
      }
    } else {
      // Schedule delayed invalidation
      setTimeout(() => {
        this.invalidateCache(reason, true);
      }, 100);
    }
  }

  async checkAndRefreshStale(): Promise<boolean> {
    if (sessionCacheManager.isStale()) {
      const lastUpdated = sessionCacheManager.getLastUpdated();
      const staleness = Date.now() - lastUpdated.getTime();

      if (staleness > this.config.stalenessThreshold) {
        logger.debug(`Session cache is stale (${staleness}ms), refreshing`);

        this.invalidateCache("staleness-check");
        return true;
      }
    }

    return false;
  }

  setRefreshCallback(callback: () => Promise<void>): void {
    this.refreshCallback = callback;
  }

  destroy(): void {
    this.destroyEventListeners();
    this.stopStalenessCheck();
    this.refreshCallback = null;
  }

  private setupEventListeners(): void {
    this.config.rules.forEach((rule) => {
      const unsubscribe = sessionEventSystem.on(rule.eventType, (event) => {
        this.handleInvalidationEvent(rule, event);
      });
      this.eventUnsubscribers.push(unsubscribe);
    });
  }

  private destroyEventListeners(): void {
    this.eventUnsubscribers.forEach((unsubscribe) => unsubscribe());
    this.eventUnsubscribers = [];
  }

  private handleInvalidationEvent(rule: InvalidationRule, event: any): void {
    const currentSession = sessionCacheManager.get();

    // Apply conditional logic if present
    if (rule.condition && !rule.condition(event.data, currentSession)) {
      return;
    }

    switch (rule.strategy) {
      case "immediate":
        this.invalidateCache(`${rule.eventType} event`, true);
        break;

      case "delayed":
        setTimeout(() => {
          this.invalidateCache(`${rule.eventType} event (delayed)`, true);
        }, rule.delay || 1000);
        break;

      case "on-next-access":
        // Mark cache as stale but don't invalidate immediately
        // The cache manager will handle this on next access
        logger.debug(`Marking cache as stale due to ${rule.eventType} event`);
        break;

      case "conditional":
        // Already handled by condition check above
        this.invalidateCache(`${rule.eventType} event (conditional)`, true);
        break;
    }
  }

  private startStalenessCheck(): void {
    if (typeof window === "undefined") return;

    // Only start if not already running
    if (this.stalenessCheckInterval) return;

    // Check for staleness every minute
    this.stalenessCheckInterval = setInterval(() => {
      this.checkAndRefreshStale();
    }, 60 * 1000);
  }

  private stopStalenessCheck(): void {
    if (this.stalenessCheckInterval) {
      clearInterval(this.stalenessCheckInterval);
      this.stalenessCheckInterval = null;
    }
  }
}

// Singleton instance
export const sessionCacheInvalidation = new SessionCacheInvalidationImpl();

// Cleanup on page unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    sessionCacheInvalidation.destroy();
  });
}
